import { Caption } from '~/shared/Typography';
import { Box, HStack, useTheme, VStack } from 'native-base';
import { GiftCardsDTO, i18nFn, useSpringBigStore } from '@springbig/shared';
import { useGiftCardStyles, useHomePageStyles } from '~/shared/styles';
import { CardTitle } from '~/pages/homePage/components/CardTitle';
import logo from '~/pages/giftCard/assets/sblogo.png';
import React, { useState, useMemo } from 'react';
import { CardBottomLink } from '~/pages/homePage/components/CardBottomLink';
import PaginationDot from 'react-native-animated-pagination-dot';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel, Pagination, Keyboard } from 'swiper';
import Barcode from 'react-barcode';

type GiftCardsProps = {
    data: GiftCardsDTO;
    t: i18nFn;
};

export const GiftCards = ({
    data,
    t,
}: GiftCardsProps) => {

    const { primary_color, pressable_text } = useHomePageStyles();
    const { merchant } = useSpringBigStore();
    const theme = useTheme();

    const [
        activeSlide,
        setActiveSlide
    ] = React.useState(0);
    const [
        isShowBarcodes,
        setIsShowBarcodes
    ] = useState<boolean>(false);

    const {
        card_color,
        card_text_color
    } = useGiftCardStyles();

    const onToggleBarCode = (): void => {

        setActiveSlide(0);
        setIsShowBarcodes(!isShowBarcodes);
    };

    const _baseHeight = 100;
    const _baseWidth = 160;
    const _offset = 2;

    const calculateHeight = useMemo(() => {

        return _baseHeight + (data.cards.length * _offset) + 'px';
    }, [data.cards.length]);

    return (
        <VStack space={4}>
            <CardTitle
                title={t(
                    'home_page.gift_cards.title',
                    [merchant?.gift_card_alias]
                )}
                icon={'faDollarSign'}
            />
            { !isShowBarcodes ?
                <>
                    <HStack
                        space={4}
                        justifyContent={'space-evenly'}
                        alignItems={'center'}
                    >

                        <VStack space={2}>
                            <VStack
                                alignItems={'center'}
                                bg={primary_color}
                                rounded={'lg'}
                                space={2}
                                minW={24}
                                p={2}
                            >
                                <Caption fontWeight={500}>
                                    { t(
                                        'giftCard.balance.cash_balance',
                                        [merchant?.gift_card_alias]
                                    ) }
                                </Caption>
                                <Caption color={pressable_text}>
                                    ${ data.cash_balance.toFixed(2) }
                                </Caption>
                            </VStack>
                            <VStack
                                alignItems={'center'}
                                bg={primary_color}
                                rounded={'lg'}
                                space={2}
                                minW={24}
                                p={2}
                            >
                                <Caption fontWeight={500}>
                                    { t('giftCard.totalCards') }
                                </Caption>
                                <Caption color={pressable_text}>
                                    { data.total_cards }
                                </Caption>
                            </VStack>
                        </VStack>

                        <VStack justifyContent={'space-between'} space={4}>
                            <Box
                                minH={calculateHeight}
                                w={_baseWidth + 'px'}
                                position={'relative'}
                                shadow={3}
                                rounded={'lg'}
                            >
                                { data.cards.sort((a, b) => a.primary - b.primary).slice(-3).map((card, index) =>

                                    <VStack
                                        key={index}
                                        position={'absolute'}
                                        justifyContent={'space-between'}
                                        borderColor={card_text_color}
                                        borderWidth={1}
                                        borderBottomWidth={0}
                                        bg={card_color}
                                        rounded={'lg'}
                                        w={_baseWidth + 'px'}
                                        h={_baseHeight + 'px'}
                                        p={2}
                                        top={_offset * index}
                                    >
                                        <HStack justifyContent={'space-between'}>
                                            <Caption
                                                color={card_text_color}
                                                alignSelf={'start'}
                                                fontWeight={500}
                                                fontSize={12}
                                            >
                                                { card.primary ? 'primary' : '' }
                                            </Caption>
                                            <img
                                                className='nav--logo'
                                                src={logo}
                                                alt='logo'
                                                width={20}
                                                style={{ alignSelf: 'flex-end', justifySelf: 'flex-end' }}
                                            />
                                        </HStack>
                                        <Caption
                                            color={card_text_color}
                                            alignSelf={'end'}
                                            fontWeight={500}
                                            fontSize={12}
                                        >
                                            ${ card.balance.toFixed(2) }
                                        </Caption>
                                    </VStack>
                                )}
                            </Box>
                            <CardBottomLink
                                button={'secondary'}
                                t={t}
                                onClick={onToggleBarCode}
                                text={'home_page.gift_cards.view_barcode'}
                            />
                        </VStack>
                    </HStack>
                    <HStack justifyContent={'space-around'} px={4} space={4}>
                        <CardBottomLink
                            t={t}
                            link={'giftCard'}
                            text={'home_page.gift_cards.manage_funds'}
                        />
                        { data.cards.length > 1 &&
                            <CardBottomLink
                                t={t}
                                link={'giftCard'}
                                text={'home_page.gift_cards.view_all'}
                            />
                        }
                    </HStack>
                </>
                :
                <>
                    <Swiper
                        spaceBetween={0}
                        navigation={false}
                        keyboard={true}
                        modules={[Pagination, Mousewheel, Keyboard]}
                        className="mySwiper"
                        style={{
                            pointerEvents: 'auto',
                            width: '100%',
                        }}
                        onSlideChange={(swiper) => {

                            setActiveSlide(swiper.snapIndex);
                        }}
                    >

                        { data.cards.sort((a, b) => b.primary - a.primary).map((card, index) =>

                            <SwiperSlide
                                style={{
                                    width: '100%',
                                    height: '100%',
                                }}
                                key={index}
                            >
                                <VStack
                                    alignItems={'center'}
                                    shadow={3}
                                    bg={primary_color}
                                    rounded={'xl'}
                                    space={2}
                                    p={4}
                                    m={2}
                                >
                                    <HStack
                                        justifyContent={'space-around'}
                                        alignItems={'baseline'}
                                        space={4}
                                    >
                                        <Caption color={pressable_text}>
                                            { t(
                                                'home_page.gift_cards.card_balance',
                                                [card.balance.toFixed(2)]
                                            ) }
                                        </Caption>
                                        { card.primary &&
                                        <Caption color={pressable_text}>
                                            { t('giftCard.primary.title') }
                                        </Caption>
                                        }
                                    </HStack>
                                    <Barcode
                                        value={card.card_number}
                                        format={'CODE128'}
                                        background={'white'}
                                    />
                                    <PaginationDot
                                        activeDotColor={theme?.colors?.primary || 'white'}
                                        curPage={activeSlide}
                                        maxPage={(data.cards ? data.cards.length : 1)}
                                    />
                                </VStack>
                            </SwiperSlide>
                        )}

                    </Swiper>
                    <CardBottomLink
                        t={t}
                        onClick={onToggleBarCode}
                        text={'home_page.gift_cards.back'}
                    />
                </>
            }
        </VStack>
    );
};
